// media items 1.8
// dependencies: can-autoplay.js
// depends on setPage() to call setMediaItems()
// depends on each $slick element to call setSlickVideo($slick)
// changelog
// 1.1 no $
// 1.2 supports audio now
// 1.3 supports fullscreen now
// 1.3.1 don't open embeds with preview when doing setPage, fix media class
// 1.3.2 new blockScrollchecker
// 1.3.3 fix for mistakenly hiding preview on touch
// 1.3.4 fix autoplay random pause
// 1.4 add set progress from progress bar
// 1.4.1 load main video with preview on play click
// 1.4.2 correct block scroll check and clean up
// 1.4.3 cleaner focusin/out
// 1.5 add fullscreen source support + remember time when switching sources
// 1.6 modular js
// 1.7 remove elFromEvent dependency
// 1.8 fix for fader update

// imports --------------------------------------------------------------------
import * as Vars from './0-vars.js';
import * as Listeners from "./1-listeners.js";
import * as Once from "./2-once.js";
import * as Scroll from "./5-scroll.js";
import CanAutoplay from 'can-autoplay'; 

// set user variables ----------------------------------------------------------
const showBlockedAutoplayHolderSelector = '.home'; // these will show OS play button

// set code variables -------------------------------------------------------
let didPageInteraction = false;
const mediaSelector = '.media__video:not(.media__video--preview), .media__audio';
let mediaTouchTimeout;
let mediaDragging = false;
let mediaDragEl;

// init function ---------------------------------------------------------------
export const initMediaItems = () => {
  // click listener ------------------------------
  document.body.addEventListener('click', event => {
    // autoplay video asap on mobile battery saver
    // touchstart doesn't count as interaction for unmuting

    // once
    if (!didPageInteraction) {
      // actual autoplay videos, not ones that have autoplay from Fader.js
      const mediaVideoEls = document.querySelectorAll('video[autoplay]:not([data-autoplay="false"]):not([data-src=""])');
      mediaVideoEls.forEach(mediaVideoEl => {
        // mediaVideoEl.play(); // done from listeners already
        const mediaHolder = mediaVideoEl.closest('.media__holder');
        if (mediaHolder) mediaHolder.classList.remove('media__holder--hidden');
      });

      didPageInteraction = true;
    }

    // tapped entire video
    let el = event.target.closest('.media');
    if (el && Vars.blTouch) {
      // show controls on touch
      const mediaEl = el;
      mediaEl.classList.add('media--touched');

      clearTimeout(mediaTouchTimeout);
      mediaTouchTimeout = setTimeout(() => {
        mediaEl.classList.remove('media--touched');
      }, Vars.transitionSlow);
    }

    // click play/pause
    el = event.target.closest('.media__btn--play-pause, .media__play');
    if (el) togglePlayPause(el.closest('.media'));

    // click mute/unmute
    el = event.target.closest('.media__btn--audio, .media__audio');
    if (el) {
      const mediaEl = el.closest('.media');
      const mediaMediaEl = mediaEl.querySelector(mediaSelector);
      if (mediaEl.classList.contains('media--muted')) {
        if (mediaMediaEl) mediaMediaEl.muted = false;
        mediaEl.classList.remove('media--muted');
      } else {
        // mute
        if (mediaMediaEl) mediaMediaEl.muted = true;
        mediaEl.classList.add('media--muted');
      }
    }

    // click grow/shrink
    el = event.target.closest('.media__btn--fullscreen');
    if (el) {
      const mediaEl = el.closest('.media');
      if (!mediaEl) return;

      if (mediaEl.classList.contains('media--fullscreen')) {
        closeFullscreenMedia(mediaEl);
      } else {
        mediaEl.classList.add('media--fullscreen');
        setVideoFaderFS(mediaEl);
        if ('checkBlockScroll' in Scroll) Scroll.checkBlockScroll();
      }
    }
  });

  // mousedown listener ------------------------------
  document.addEventListener('mousedown', event => {
    let el = event.target.closest('.media__progress');
    if (el) {
      setMediaProgress(event, el, 'mouse');
      mediaDragEl = el;
      mediaDragging = true;
    }
  });

  // touchstart listener ------------------------------
  document.body.addEventListener('touchstart', event => {
    let el = event.target.closest('.media__progress');
    if (el) {
      setMediaProgress(event, el, 'touch');
      mediaDragEl = el;
      mediaDragging = true;
    }
  });

  // mousemove listener ------------------------------
  document.addEventListener('mousemove', event => {
    if (mediaDragging) {
      setMediaProgress(event, mediaDragEl, 'mouse');
    }
  });

  // touchmove listener ------------------------------
  document.body.addEventListener('touchmove', event => {
    if (mediaDragging) {
      setMediaProgress(event, mediaDragEl, 'touch');
    }
  });

  // mouseup listener ------------------------------
  document.addEventListener('mouseup', event => {
    mediaDragging = false;
  });

  // touchend listener ------------------------------
  document.body.addEventListener('touchend', event => {
    mediaDragging = false;
  });

  // keyboard focus on media buttons ----------------
  document.body.addEventListener('focusin', event => {
    let el = event.target.closest('.media__btn');
    if (el) {
      const mediaEl = el.closest('.media');
      if (mediaEl) mediaEl.classList.add('media--focus');
    }
  });

  // keyboard blur on media buttons ----------------
  document.body.addEventListener('focusout', event => {
    let el = event.target.closest('.media__btn');
    if (el) {
      const mediaEl = el.closest('.media');
      if (mediaEl) mediaEl.classList.remove('media--focus');
    }
  });

  document.addEventListener('keyup', event => {
    const fsMediaEl = document.querySelector('.media--fullscreen');
    if (!fsMediaEl) return;

    if (event.code === 'Escape') {
      closeFullscreenMedia(fsMediaEl);
    } else if (event.code === 'Space') {
      togglePlayPause(fsMediaEl);
    }
  });

  document.addEventListener('keydown', event => {
    // block keyboard scrolling on fullscreen media
    if (document.querySelector('.media--fullscreen')) {
      if (['Space', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].indexOf(event.code) > -1) {
        event.preventDefault();
      }
    }
  }, false);
};

// must be called from setPage -------------------------------------------------
export const setMediaItems = () => {
  // console.log('setMediaItems');

  CanAutoplay.video({timeout: 1000, muted: true, inline: true}).then(({result, error}) => {
    // console.log({result, didPageInteraction});

    // even on battery saver, after a page interaction you can always autoplay video
    if (!result && !didPageInteraction && Vars.blTouch) {
      // can't autoplay video's, so hide them
      // actual autoplay video's, not ones that have autoplay from Fader.js
      const mediaVideoEls = document.querySelectorAll('video[autoplay]:not([data-autoplay="false"])');
      mediaVideoEls.forEach(mediaVideoEl => {
        // If the media__video element can't autoplay and is not in the noHideAutoplayHolder, hide it
        const showBlockedAutoplayHolder = mediaVideoEl.closest(showBlockedAutoplayHolderSelector);
        if (!showBlockedAutoplayHolder) {
          const mediaHolder = mediaVideoEl.closest('.media__holder');
          if (mediaHolder) mediaHolder.classList.add('media__holder--hidden');
        }
      });
    }
  });

  let mediaMediaEls = document.querySelectorAll('.media__video:not([autoplay]):not(.media__video--preview), .media__audio');

  // console.log({mediaMediaEls});

  // remove observer from elements first
  mediaSrcObserver.disconnect();

  mediaMediaEls.forEach(mediaMediaEl => {
    mediaSrcObserver.observe(mediaMediaEl, {
      attributes: true, // configure it to listen to attribute changes
    });

    // remove Listeners for possible re-init
    mediaMediaEl.removeEventListener('ended', event => { });
    mediaMediaEl.removeEventListener('webkitendfullscreen', event => { });
    mediaMediaEl.removeEventListener('play', event => { });
    mediaMediaEl.removeEventListener('pause', event => { });
    mediaMediaEl.removeEventListener('timeupdate', event => { });

    mediaMediaEl.addEventListener('ended', event => {
      // console.log('ended', {autoplay: event.currentTarget.autoplay});
      // doesn't fire on iOS (sometimes)
      endedMedia(event.currentTarget.closest('.media'));
    });

    mediaMediaEl.addEventListener('webkitendfullscreen', event => {
    // console.log('fullscreen');
    // This pauses the media, so do correct actions
      const mediaEl = event.currentTarget.closest('.media');
      pauseMedia(mediaEl);
    });

    mediaMediaEl.addEventListener('play', event => {
      // console.log('play');
      const mediaEl = event.currentTarget.closest('.media');
      if (mediaEl) {
        mediaEl.classList.add('media--playing');

        const mediaVideoEl = mediaEl.querySelector('.media__video');
        // console.log(mediaVideoEl.dataset.mayplay);
        if (mediaVideoEl && mediaVideoEl.dataset.mayplay === 'true') { // mayplay comes from fader (false) or play btn (true)
          mediaEl.classList.add('media--started'); // hides preview
        }
      }
    });

    mediaMediaEl.addEventListener('pause', event => {
      // console.log('pause');
      const mediaEl = event.currentTarget.closest('.media');
      if (mediaEl) mediaEl.classList.remove('media--playing');
    });
  });

  // fix for random autoplay pausing
  mediaMediaEls = document.querySelectorAll('.media__video[autoplay]');
  mediaMediaEls.forEach(mediaMediaEl => {
    mediaMediaEl.addEventListener('pause', event => {
      // console.log('pause autoplay');
      event.currentTarget.play();
    });
  });

  mediaMediaEls = document.querySelectorAll('.media__video:not(.media__video--preview), .media__audio');
  mediaMediaEls.forEach(mediaMediaEl => {
    mediaMediaEl.addEventListener('timeupdate', event => {
      showMediaTime(event.currentTarget);
    });
  });

  // in timeout not to interfere with the load event
  setTimeout(() => {
    // open/play video's in the current selector
    if (typeof $historySelector === 'undefined') return;

    const historySelectorEl = document.querySelector($historySelector);
    if (historySelectorEl) {
      if (typeof openVideoEmbeds === 'function') openVideoEmbeds(historySelectorEl, false);
      playAutoplayVideos(historySelectorEl);
    }
    // both skip foldouts

    let otherSelector;
    if ($historySelector == '.popover') {
      otherSelector = '.main';
    } else {
      otherSelector = '.popover';
    }

    // stop video's in the other selector
    const otherEl = document.querySelector(otherSelector);
    if (otherEl) {
      if (typeof closeVideoEmbeds === 'function') closeVideoEmbeds(otherEl);
      pauseMediaItems(otherEl);
    }
  }, Vars.transitionDefault);
};

// must be called when setting slick -------------------------------------------
// const setSlickVideo = $slickSlider => {
//   // pause other medias
//   $slickSlider.on('afterChange', (event, slick, currentSlide) => {
//     // console.log('afterChange');
//     const otherSlides = '.slick-slide:not([data-slick-index="' + currentSlide + '"])';
//     const $otherSlides = $(event.currentTarget).find(otherSlides);
//     $otherSlides.each((index, element) => {
//       const $media = $(element).find(mediaSelector);
//       // console.log({ $media });
//       $media.trigger('pause');
//       $(element).find('.media').removeClass('media--playing');
//     });
//   });

//   // if autoplay, continue current media
//   $slickSlider.on('beforeChange', (event, slick, currentSlide, nextSlide) => {
//     const slide = '.slick-slide[data-slick-index="' + nextSlide + '"]';
//     const $currentSlide = $(event.currentTarget).find(slide);
//     const $media = $currentSlide.find(mediaSelector);
//     // console.log('beforeChange', { slide, $currentSlide, $media }, $media.prop('autoplay'));
//     if ($media.length && $media.prop('autoplay') === true) $media.trigger('play');
//     // media--playing doesn't get added for autoplay medias
//   });
// };

// functions -------------------------------------------------------------------
const setMediaProgress = (event, progressEl, inputType) => {
  const mediaEl = progressEl.closest('.media');
  if (!mediaEl) return;

  const mediaMediaEl = mediaEl.querySelector(mediaSelector);
  if (!mediaMediaEl) return;

  mediaEl.classList.add('media--started'); // hides preview

  let x;
  if (inputType == 'touch') {
    x = event.touches[0].clientX - progressEl.getBoundingClientRect().left;
  } else {
    x = event.clientX - progressEl.getBoundingClientRect().left;
  }

  const percentage = x / progressEl.offsetWidth * 100;
  mediaMediaEl.currentTime = mediaMediaEl.duration / 100 * percentage;
  showMediaTime(mediaMediaEl, mediaMediaEl.currentTime);

  mediaEl.classList.add('media--touched');

  clearTimeout(mediaTouchTimeout);
  mediaTouchTimeout = setTimeout(() => {
    mediaEl.classList.remove('media--touched');
  }, Vars.transitionSlow);
};

const closeFullscreenMedia = mediaEl => {
  // hide holder, for safari fix
  // const zoomEl = mediaEl.closest('.zoom--active');
  // if (zoomEl) zoomEl.classList.remove('zoom--active');

  mediaEl.classList.remove('media--fullscreen');
  setVideoFaderFS(mediaEl);
  if ('checkBlockScroll' in Scroll) Scroll.checkBlockScroll();

  // show holder, for safari fix
  // if (zoomEl) zoomEl.classList.add('zoom--active');
};

// load fullscreen video if available
const setVideoFaderFS = mediaEl => {
  if (!mediaEl) return;

  const mediaMediaEl = mediaEl.querySelector(mediaSelector);
  if (!mediaMediaEl) return;

  // this is the element that initialises the loading of a fullscreen source
  const mediaLoaderEl = mediaMediaEl.closest('.fader--loader, .fader--loading, .fader--loaded');

  if (mediaLoaderEl) {
    if (mediaEl.classList.contains('media--fullscreen')) {
      mediaLoaderEl.classList.add('fader--fullscreen'); // removes transition so it remains fullscreen
    } else {
      mediaLoaderEl.classList.remove('fader--fullscreen');
    }

    // begins possible load of fullscreen source
    if ('fader' in Once) Once.fader.setFaders();
  }
};

export const togglePlayPause = mediaEl => {
  if (!mediaEl) return;

  if (mediaEl.classList.contains('media--playing')) {
    pauseMedia(mediaEl);
  } else {
    pauseMediaItems(document.body);

    // not playing, so play
    const mediaMediaEl = mediaEl.querySelector(mediaSelector);

    // console.log({mediaEl, mediaMediaEl});

    if (mediaMediaEl) {
      mediaMediaEl.dataset.mayplay = 'true'; // set mayplay so preview can fade

      if (!mediaMediaEl.src) {
        let classname = mediaMediaEl.getAttribute('class');
        classname += ' ' + mediaMediaEl.dataset.fader;
        mediaMediaEl.setAttribute('class', classname);
        if ('fader' in Once) Once.fader.setFaders();

        setTimeout(() => mediaMediaEl.play(), 100); // because of how fader loads, need to wait a bit before playing
      } else {
        mediaMediaEl.play();
      }
    }
  }
};

const showMediaTime = (mediaMediaEl, seconds = mediaMediaEl.currentTime) => {
  // console.log('showMediaTime', {mediaMediaEl, seconds});
  if (!mediaMediaEl) return;

  // save current time for when switching sources
  if (seconds != 0) mediaMediaEl.dataset.seconds = seconds;

  let timeString;
  if (seconds < 3600) {
    timeString = new Date(mediaMediaEl.currentTime * 1000).toISOString().slice(14, 19);
  } else {
    timeString = new Date(mediaMediaEl.currentTime * 1000).toISOString().slice(12, 19);
  }

  const timeEl = mediaMediaEl.closest('.media').querySelector('.media__time');
  if (timeEl) timeEl.innerHTML = timeString;

  let percentage = seconds / mediaMediaEl.duration * 100;
  if (isNaN(percentage)) percentage = 0;

  const progressEl = mediaMediaEl.closest('.media').querySelector('.media__progress-bar');
  if (progressEl) progressEl.style.left = percentage + '%';

  if (mediaMediaEl.currentTime == mediaMediaEl.duration && seconds != 0) {
    // media ended, but 'ended' doensn't fire on ios, so use this
    endedMedia(mediaMediaEl.closest('.media'));
  }
};

const endedMedia = mediaEl => {
  pauseMedia(mediaEl);

  mediaEl.classList.remove('media--started');
  const mediaMediaEl = mediaEl.querySelector(mediaSelector);
  if (mediaMediaEl) mediaMediaEl.currentTime = 0;
};

const pauseMedia = mediaEl => {
  // console.log('pauseMedia', {mediaEl});
  const mediaMediaEl = mediaEl.querySelector(mediaSelector);
  if (mediaMediaEl) mediaMediaEl.pause();

  // audioBtnEls.forEach(audioBtnEl => audioBtnEl.classList.remove('audio-btn--visible'));
};

const playAutoplayVideos = holderEl => {
  // console.log('playAutoplayVideos', holderEl);

  const mediaMediaEls = holderEl.querySelectorAll(mediaSelector);
  mediaMediaEls.forEach(mediaMediaEl => {
    // skips closed foldouts
    const foldout = mediaMediaEl.closest('.fold-out');
    // console.log({mediaMediaEl, foldout});
    if (mediaMediaEl.autoplay && (!foldout || foldout.classList.contains('fold-out--opened'))) {
      mediaMediaEl.play();
    }
  });
};

export const pauseMediaItems = holderEl => {
  const mediaMediaEls = holderEl.querySelectorAll(mediaSelector);
  mediaMediaEls.forEach(mediaMediaEl => {
    const mediaEl = mediaMediaEl.closest('.media');
    if (mediaEl) pauseMedia(mediaEl);
  });
};

const mediaSrcObserver = new MutationObserver(mutations => {
  mutations.forEach(mutation => {
    if (mutation.type === 'attributes' && mutation.attributeName === 'src') {
      // console.log('src changed to: ' + mutation.target.src);
      const mediaEl = mutation.target.closest('.media');
      if (mediaEl) mediaEl.classList.remove('media--playing');

      // set video with new source to the same time
      const mediaMediaEl = mutation.target;
      const {seconds} = mediaMediaEl.dataset;
      if (seconds) {
        mediaMediaEl.currentTime = seconds;
        showMediaTime(mediaMediaEl, seconds);
      }
    }
  });
});
